/**
 * WeaveIn.ai Platform - Enrollments Hook
 * React hook for enrollment management
 */

import { useState, useEffect, useCallback } from 'react';
import {
  enrollmentsAPI,
  Enrollment,
  EnrollmentCreate,
  EnrollmentListResponse,
  EnrollmentStatus,
  Activity,
  ActivityCreate,
  Document,
  DocumentCreate,
  Note,
  NoteCreate,
  Goal,
  GoalCreate,
} from '@/services/api/enrollments';

interface UseEnrollmentsOptions {
  page?: number;
  page_size?: number;
  member_id?: number;
  program_id?: number;
  status?: EnrollmentStatus;
  search?: string;
  autoFetch?: boolean;
}

interface UseEnrollmentsReturn {
  enrollments: Enrollment[];
  total: number;
  page: number;
  pageSize: number;
  loading: boolean;
  error: string | null;
  createEnrollment: (data: EnrollmentCreate) => Promise<Enrollment | null>;
  updateEnrollment: (id: number, data: Partial<EnrollmentCreate>) => Promise<Enrollment | null>;
  deleteEnrollment: (id: number) => Promise<boolean>;
  getEnrollment: (id: number) => Promise<Enrollment | null>;
  refetch: () => Promise<void>;
  setPage: (page: number) => void;
  setFilters: (filters: { member_id?: number; program_id?: number; status?: EnrollmentStatus; search?: string }) => void;
}

export default function useEnrollments(options: UseEnrollmentsOptions = {}): UseEnrollmentsReturn {
  const {
    page: initialPage = 1,
    page_size = 50,
    member_id: initialMemberId,
    program_id: initialProgramId,
    status: initialStatus,
    search: initialSearch,
    autoFetch = true,
  } = options;

  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPageState] = useState(initialPage);
  const [memberId, setMemberId] = useState(initialMemberId);
  const [programId, setProgramId] = useState(initialProgramId);
  const [status, setStatus] = useState(initialStatus);
  const [search, setSearch] = useState(initialSearch);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Sync search state with prop changes and reset page to 1
  useEffect(() => {
    if (initialSearch !== search) {
      setSearch(initialSearch);
      setPageState(1); // Reset to first page when search changes
    }
  }, [initialSearch]);

  // Wrapper for setPage to allow external control
  const setPage = (newPage: number) => {
    setPageState(newPage);
  };

  const fetchEnrollments = useCallback(async () => {
    console.log('🔍 useEnrollments: Fetching enrollments with params:', {
      page,
      page_size,
      member_id: memberId,
      program_id: programId,
      status,
      search,
    });
    setLoading(true);
    setError(null);

    try {
      const response: EnrollmentListResponse = await enrollmentsAPI.listEnrollments({
        page,
        page_size,
        member_id: memberId,
        program_id: programId,
        status,
        search: search || undefined,
      });

      console.log('✅ useEnrollments: API Response:', response);
      console.log(`📊 useEnrollments: Got ${response.enrollments.length} enrollments, total=${response.total}`);

      setEnrollments(response.enrollments);
      setTotal(response.total);
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to fetch enrollments';
      setError(errorMessage);
      console.error('❌ useEnrollments: Error fetching enrollments:', err);
    } finally {
      setLoading(false);
    }
  }, [page, page_size, memberId, programId, status, search]);

  useEffect(() => {
    if (autoFetch) {
      fetchEnrollments();
    }
  }, [autoFetch, fetchEnrollments]);

  const createEnrollment = async (data: EnrollmentCreate): Promise<Enrollment | null> => {
    setLoading(true);
    setError(null);

    try {
      const newEnrollment = await enrollmentsAPI.createEnrollment(data);

      // Optimistic update - add to list
      setEnrollments((prev) => [newEnrollment, ...prev]);
      setTotal((prev) => prev + 1);

      return newEnrollment;
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to create enrollment';
      setError(errorMessage);
      console.error('Error creating enrollment:', err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const updateEnrollment = async (
    id: number,
    data: Partial<EnrollmentCreate>
  ): Promise<Enrollment | null> => {
    setLoading(true);
    setError(null);

    try {
      const updatedEnrollment = await enrollmentsAPI.updateEnrollment(id, data);

      // Update in local state
      setEnrollments((prev) =>
        prev.map((enrollment) => (enrollment.id === id ? updatedEnrollment : enrollment))
      );

      return updatedEnrollment;
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to update enrollment';
      setError(errorMessage);
      console.error('Error updating enrollment:', err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const deleteEnrollment = async (id: number): Promise<boolean> => {
    setLoading(true);
    setError(null);

    try {
      await enrollmentsAPI.deleteEnrollment(id);

      // Remove from local state
      setEnrollments((prev) => prev.filter((enrollment) => enrollment.id !== id));
      setTotal((prev) => prev - 1);

      return true;
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to delete enrollment';
      setError(errorMessage);
      console.error('Error deleting enrollment:', err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const getEnrollment = async (id: number): Promise<Enrollment | null> => {
    setLoading(true);
    setError(null);

    try {
      const enrollment = await enrollmentsAPI.getEnrollment(id);
      return enrollment;
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to fetch enrollment';
      setError(errorMessage);
      console.error('Error fetching enrollment:', err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const setFilters = (filters: { member_id?: number; program_id?: number; status?: EnrollmentStatus; search?: string }) => {
    setMemberId(filters.member_id);
    setProgramId(filters.program_id);
    setStatus(filters.status);
    setSearch(filters.search);
    setPage(1); // Reset to first page when filters change
  };

  return {
    enrollments,
    total,
    page,
    pageSize: page_size,
    loading,
    error,
    createEnrollment,
    updateEnrollment,
    deleteEnrollment,
    getEnrollment,
    refetch: fetchEnrollments,
    setPage,
    setFilters,
  };
}

// ============================================================================
// ADDITIONAL HOOKS FOR ENROLLMENT SUB-RESOURCES
// ============================================================================

export function useEnrollmentActivities(enrollmentId: number) {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchActivities = useCallback(async () => {
    if (!enrollmentId) return;

    setLoading(true);
    setError(null);

    try {
      const data = await enrollmentsAPI.listActivities(enrollmentId);
      setActivities(data);
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to fetch activities';
      setError(errorMessage);
      console.error('Error fetching activities:', err);
    } finally {
      setLoading(false);
    }
  }, [enrollmentId]);

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  const createActivity = async (data: ActivityCreate): Promise<Activity | null> => {
    try {
      const newActivity = await enrollmentsAPI.createActivity(data);
      setActivities((prev) => [newActivity, ...prev]);
      return newActivity;
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to create activity';
      setError(errorMessage);
      return null;
    }
  };

  const updateActivity = async (id: number, data: Partial<ActivityCreate>): Promise<Activity | null> => {
    try {
      const updated = await enrollmentsAPI.updateActivity(id, data);
      setActivities((prev) => prev.map((a) => (a.id === id ? updated : a)));
      return updated;
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to update activity';
      setError(errorMessage);
      return null;
    }
  };

  const deleteActivity = async (id: number): Promise<boolean> => {
    try {
      await enrollmentsAPI.deleteActivity(id);
      setActivities((prev) => prev.filter((a) => a.id !== id));
      return true;
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to delete activity';
      setError(errorMessage);
      return false;
    }
  };

  return {
    activities,
    loading,
    error,
    createActivity,
    updateActivity,
    deleteActivity,
    refetch: fetchActivities,
  };
}

export function useEnrollmentDocuments(enrollmentId: number) {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchDocuments = useCallback(async () => {
    if (!enrollmentId) return;

    setLoading(true);
    setError(null);

    try {
      const data = await enrollmentsAPI.listDocuments(enrollmentId);
      setDocuments(data);
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to fetch documents';
      setError(errorMessage);
      console.error('Error fetching documents:', err);
    } finally {
      setLoading(false);
    }
  }, [enrollmentId]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const createDocument = async (data: DocumentCreate): Promise<Document | null> => {
    try {
      const newDocument = await enrollmentsAPI.createDocument(data);
      setDocuments((prev) => [newDocument, ...prev]);
      return newDocument;
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to create document';
      setError(errorMessage);
      return null;
    }
  };

  const deleteDocument = async (id: number): Promise<boolean> => {
    try {
      await enrollmentsAPI.deleteDocument(id);
      setDocuments((prev) => prev.filter((d) => d.id !== id));
      return true;
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to delete document';
      setError(errorMessage);
      return false;
    }
  };

  return {
    documents,
    loading,
    error,
    createDocument,
    deleteDocument,
    fetchDocuments,
    refetch: fetchDocuments,
  };
}

export function useEnrollmentNotes(enrollmentId: number) {
  const [notes, setNotes] = useState<Note[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchNotes = useCallback(async () => {
    if (!enrollmentId) return;

    setLoading(true);
    setError(null);

    try {
      const data = await enrollmentsAPI.listNotes(enrollmentId);
      setNotes(data);
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to fetch notes';
      setError(errorMessage);
      console.error('Error fetching notes:', err);
    } finally {
      setLoading(false);
    }
  }, [enrollmentId]);

  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  const createNote = async (data: NoteCreate): Promise<Note | null> => {
    try {
      const newNote = await enrollmentsAPI.createNote(data);
      setNotes((prev) => [newNote, ...prev]);
      return newNote;
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to create note';
      setError(errorMessage);
      return null;
    }
  };

  const updateNote = async (id: number, data: Partial<NoteCreate>): Promise<Note | null> => {
    try {
      const updated = await enrollmentsAPI.updateNote(id, data);
      setNotes((prev) => prev.map((n) => (n.id === id ? updated : n)));
      return updated;
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to update note';
      setError(errorMessage);
      return null;
    }
  };

  const deleteNote = async (id: number): Promise<boolean> => {
    try {
      await enrollmentsAPI.deleteNote(id);
      setNotes((prev) => prev.filter((n) => n.id !== id));
      return true;
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to delete note';
      setError(errorMessage);
      return false;
    }
  };

  return {
    notes,
    loading,
    error,
    createNote,
    updateNote,
    deleteNote,
    refetch: fetchNotes,
  };
}

export function useEnrollmentGoals(enrollmentId: number) {
  const [goals, setGoals] = useState<Goal[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchGoals = useCallback(async () => {
    if (!enrollmentId) return;

    setLoading(true);
    setError(null);

    try {
      const data = await enrollmentsAPI.listGoals(enrollmentId);
      setGoals(data);
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to fetch goals';
      setError(errorMessage);
      console.error('Error fetching goals:', err);
    } finally {
      setLoading(false);
    }
  }, [enrollmentId]);

  useEffect(() => {
    fetchGoals();
  }, [fetchGoals]);

  const createGoal = async (data: GoalCreate): Promise<Goal | null> => {
    try {
      const newGoal = await enrollmentsAPI.createGoal(data);
      setGoals((prev) => [newGoal, ...prev]);
      return newGoal;
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to create goal';
      setError(errorMessage);
      return null;
    }
  };

  const updateGoal = async (id: number, data: Partial<GoalCreate>): Promise<Goal | null> => {
    try {
      const updated = await enrollmentsAPI.updateGoal(id, data);
      setGoals((prev) => prev.map((g) => (g.id === id ? updated : g)));
      return updated;
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to update goal';
      setError(errorMessage);
      return null;
    }
  };

  const deleteGoal = async (id: number): Promise<boolean> => {
    try {
      await enrollmentsAPI.deleteGoal(id);
      setGoals((prev) => prev.filter((g) => g.id !== id));
      return true;
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to delete goal';
      setError(errorMessage);
      return false;
    }
  };

  return {
    goals,
    loading,
    error,
    createGoal,
    updateGoal,
    deleteGoal,
    refetch: fetchGoals,
  };
}
